/**
 * EdiConnectorsSection · Feb 2026 · Iter 209 (Phase B · aggregate view)
 * ─────────────────────────────────────────────────────────────────────
 * Renders EDI trading-partner connections as PEERS of the direct sales
 * channels — same visual language, same "connect / manage" affordances.
 * Lives inside the "All buyers" (aggregate) view of Store Setup so
 * sellers see a single connectors surface for both marketplace channels
 * (Amazon, TikTok, …) and B2B retailer EDI relationships (Macy's,
 * Nordstrom, TJX, Target Plus, …).
 *
 * State model:
 *   · Load /edi/pending-partners → array of {buyer_id, buyer_name,
 *     trading_partner_id, enabled, orders_ingested, last_850_at}
 *   · Empty state → "Connect EDI" button opens picker dialog to pick a
 *     wholesale buyer to onboard.
 *   · Populated → grid of buyer cards; click any card → opens a sheet
 *     with the full BuyerEdiPanel (settings + ingest + emit + phase-2
 *     coming-soon block) reused verbatim.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../../../lib/api";
import { toast } from "sonner";
import {
  FileText, Building2, Plus, Loader2, ChevronRight, ShieldCheck, Clock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import BuyerEdiPanel from "./BuyerEdiPanel";

export default function EdiConnectorsSection({ buyers = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [drawerBuyer, setDrawerBuyer] = useState(null);

  const load = () => {
    setLoading(true);
    // No aggregate endpoint yet — fan out to each buyer's settings.
    // Small parallelism keeps this fast for the common case of <20 buyers.
    Promise.all((buyers || []).map(b =>
      api.get(`/edi/buyers/${b.id}/settings`)
        .then(r => ({ buyer: b, settings: r.data }))
        .catch(() => ({ buyer: b, settings: null }))
    ))
      .then(all => {
        // Only surface buyers where EDI has been enabled at least once.
        setRows(all.filter(x => x.settings?.enabled));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [buyers.length]);

  const enabledBuyerIds = useMemo(() => new Set(rows.map(r => r.buyer.id)), [rows]);
  const candidates = useMemo(
    () => (buyers || []).filter(b => !enabledBuyerIds.has(b.id)),
    [buyers, enabledBuyerIds],
  );

  const openBuyer = (b) => {
    setPickerOpen(false);
    setDrawerBuyer(b);
  };

  return (
    <div data-testid="edi-connectors-section">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55 mb-1.5 flex items-center gap-2">
            EDI trading partners
            <span
              data-testid="edi-connectors-coming-soon"
              className="inline-flex items-center gap-1 text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-violet-600 text-white shadow-sm normal-case"
            >
              <Clock className="w-2.5 h-2.5"/> Coming soon
            </span>
          </div>
          <div className="text-[13.5px] font-medium">Connect retailers using X12 EDI</div>
          <div className="text-[11.5px] text-foreground/55 max-w-2xl mt-0.5">
            Macy&apos;s, Nordstrom, TJX, Target Plus, Bloomingdale&apos;s, JCPenney — retail
            trading partners send POs as 850 files, and we auto-generate the 855 · 856 ·
            810 replies. Wire one buyer at a time.
          </div>
          <div className="text-[10.5px] text-violet-700/80 mt-1.5 italic">
            Beta · X12 850 ingest + 855/856/810 emit working today · SPS Commerce /
            DiCentral / TrueCommerce SFTP automation lands in Phase 2.
          </div>
        </div>
        {rows.length > 0 && (
          <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/50 pt-1.5">
            {rows.length} enabled · {rows.reduce((n, r) => n + (r.settings?.orders_ingested || 0), 0)} POs ingested
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center text-foreground/40">
          <Loader2 className="w-4 h-4 animate-spin"/>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="edi-connectors-grid">
          {rows.map(({ buyer, settings }) => (
            <button
              key={buyer.id}
              onClick={() => openBuyer(buyer)}
              data-testid={`edi-connector-card-${buyer.id}`}
              className="text-left rounded-2xl border border-border bg-white p-4 hover:border-violet-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-700 grid place-items-center shrink-0">
                  <FileText className="w-4 h-4"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold truncate">
                    {buyer.company || buyer.name}
                  </div>
                  <div className="text-[11px] text-foreground/55 mt-0.5 truncate">
                    ISA sender <span className="mono">{settings?.trading_partner_id || "—"}</span>
                    {settings?.vendor_code && (<> · Vendor <span className="mono">{settings.vendor_code}</span></>)}
                  </div>
                  <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/40 mt-1 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-600"/>
                    {settings?.orders_ingested || 0} POs ingested
                    {settings?.last_850_at && (
                      <> · <Clock className="w-3 h-3"/> {new Date(settings.last_850_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-foreground/40 shrink-0 mt-1"/>
              </div>
            </button>
          ))}
          {/* Connect-EDI card — same footprint as the other connector cards */}
          <button
            onClick={() => {
              if ((candidates || []).length === 0) {
                toast("Every wholesale buyer already has EDI enabled.", { icon: "ℹ️" });
                return;
              }
              setPickerOpen(true);
            }}
            data-testid="edi-connector-add"
            className="rounded-2xl border border-dashed border-border bg-white/60 p-4 flex flex-col items-center justify-center text-center hover:border-violet-300 hover:bg-violet-50/40 transition-colors min-h-[104px]"
          >
            <div className="w-9 h-9 rounded-lg bg-secondary text-foreground/60 grid place-items-center mb-2">
              <Plus className="w-4 h-4"/>
            </div>
            <div className="text-[12.5px] font-medium">Connect EDI for a buyer</div>
            <div className="text-[10.5px] text-foreground/50 mt-0.5">
              Wire a retailer using X12 850 / 855 / 856 / 810
            </div>
          </button>
        </div>
      )}

      {/* Buyer-picker dialog (opens when the seller clicks "Connect EDI") */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md" data-testid="edi-buyer-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-4 h-4"/>Pick a buyer to enable EDI
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2 space-y-1">
            {(candidates || []).map(b => (
              <button
                key={b.id}
                onClick={() => openBuyer(b)}
                data-testid={`edi-picker-buyer-${b.id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-md hover:bg-secondary/60"
              >
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center shrink-0">
                  <Building2 className="w-3.5 h-3.5"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{b.company || b.name}</div>
                  {b.email && <div className="text-[10.5px] text-foreground/50 truncate">{b.email}</div>}
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-foreground/40"/>
              </button>
            ))}
            {(candidates || []).length === 0 && (
              <div className="text-[12px] text-foreground/50 px-3 py-8 text-center">
                No eligible buyers. Every wholesale buyer already has EDI enabled.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full EDI panel in a right-side sheet · reuses BuyerEdiPanel verbatim */}
      <Sheet open={!!drawerBuyer} onOpenChange={(v) => !v && (setDrawerBuyer(null), load())}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="edi-drawer"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-700"/>
              EDI · {drawerBuyer?.company || drawerBuyer?.name}
            </SheetTitle>
            <SheetDescription>
              Trading-partner settings + inbound 850 ingest + outbound 855 / 856 / 810.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {drawerBuyer && <BuyerEdiPanel buyer={drawerBuyer} />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
